import CTNDropdown from '@ctn/ui/components/ctn-dropdown';
import CTNInput from '@ctn/ui/components/ctn-input';
import CTNInputNest from '@ctn/ui/components/ctn-input-nest';
import classNames from 'classnames';
import { useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';

import useOptionsStore from '@/store/options.store';

import { Cleaners } from '@/helpers/Cleaners/fieldDataCleaner';

import { type SujetoForm } from '../config';

type Props = {
	readonly className?: string;
	readonly title?: string;
	readonly isDisplay?: boolean;
	readonly isDisabled?: boolean;
};
const DatosPersona = ({ className, title, isDisplay, isDisabled }: Props) => {
	const {
		control,
		formState: { errors, dirtyFields },
		trigger,
		watch,
		setValue,
		getValues,
	} = useFormContext<SujetoForm>();

	const paises = useOptionsStore((state) => state.options.paises) ?? [];
	const idTipoDocumentoIdent = watch('idTipoDocumentoIdent');
	const tipoDocumentoIdent = useOptionsStore((state) => state.options.tipoDocumentoIdent) ?? [];
	const tipoDocumento = watch('idTipoDocumentoIdent');
	const prefijoPaises = useOptionsStore((state) => state.options.prefijoPaises) ?? [];

	const classNameDatosPersona = useMemo(() => classNames(className), [className]);

	const cleanNumeroDocumento = (value: string) => {
		if (tipoDocumento === '1') {
			const limpio = Cleaners.dni(value);
			setValue('numeroDocumento', limpio);
		}
		if (tipoDocumento === '2') {
			const limpio = Cleaners.nie(value);
			setValue('numeroDocumento', limpio);
		}
	};

	useEffect(() => {
		const currentNumber = getValues('numeroDocumento');
		if (dirtyFields.numeroDocumento) {
			if (currentNumber?.length === 9) {
				cleanNumeroDocumento(currentNumber);
			}
			trigger('numeroDocumento');
		}
	}, [idTipoDocumentoIdent]);

	return (
		<fieldset className={classNameDatosPersona}>
			{title && <legend className="c-pnp-heading-h4">{title}</legend>}
			<div
				className={classNames('', {
					['[ c-pnp-form-group ] [ u-flow ]']: isDisplay,
					['[ u-mt-16 ]']: title,
				})}
			>
				<div className="c-pnp-flow-grid">
					<CTNDropdown
						filter
						required={!isDisplay}
						displayOnly={isDisplay}
						condensed={isDisplay}
						noFeedBack={isDisplay}
						disabled={isDisabled}
						options={paises}
						classNameField="c-pnp-flow-grid__nac"
						name="nacionalidad"
						label="Nacionalidad"
						control={control}
						appendTo='body'
						error={errors.nacionalidad?.message}
					/>
					<CTNDropdown
						required={!isDisplay}
						displayOnly={isDisplay}
						condensed={isDisplay}
						noFeedBack={isDisplay}
						disabled={isDisabled}
						options={tipoDocumentoIdent}
						classNameField="c-pnp-flow-grid__doc"
						name="idTipoDocumentoIdent"
						label="Tipo de documento"
						control={control}
						appendTo='body'
						error={errors.idTipoDocumentoIdent?.message}
					/>
					<CTNInput
						required={!isDisplay}
						displayOnly={isDisplay}
						condensed={isDisplay}
						noFeedBack={isDisplay}
						disabled={isDisabled}
						type="text"
						classNameField="c-pnp-flow-grid__numdoc"
						name="numeroDocumento"
						label="Número de documento"
						control={control}
						error={errors.numeroDocumento?.message}
						maxLength={20}
						onBlur={(e) => {
							cleanNumeroDocumento(e.target.value);
						}}
						onChange={(e) => {
							cleanNumeroDocumento(e.target.value);
						}}
					/>
				</div>
				<div className="c-pnp-flow-grid">
					<CTNInput
						required={!isDisplay}
						displayOnly={isDisplay}
						condensed={isDisplay}
						noFeedBack={isDisplay}
						disabled={isDisabled}
						type="text"
						classNameField="c-pnp-flow-grid__nom"
						name="nombre"
						label="Nombre"
						control={control}
						maxLength={150}
						error={errors.nombre?.message}
						onBlur={(e) => {
							const limpio = Cleaners.texto(e.target.value);
							e.target.value = limpio;
							setValue('nombre', limpio);
						}}
						onChange={(e) => {
							const limpio = Cleaners.texto(e.target.value);
							e.target.value = limpio;
							setValue('nombre', limpio);
						}}
					/>
					<CTNInput
						required={!isDisplay}
						displayOnly={isDisplay}
						condensed={isDisplay}
						noFeedBack={isDisplay}
						disabled={isDisabled}
						type="text"
						classNameField="c-pnp-flow-grid__ape"
						name="apellido1RacSoc"
						label="Primer apellido"
						control={control}
						maxLength={150}
						error={errors.apellido1RacSoc?.message}
						onBlur={(e) => {
							const limpio = Cleaners.texto(e.target.value);
							e.target.value = limpio;
							setValue('apellido1RacSoc', limpio);
						}}
						onChange={(e) => {
							const limpio = Cleaners.texto(e.target.value);
							e.target.value = limpio;
							setValue('apellido1RacSoc', limpio);
						}}
					/>
					<CTNInput
						disabled={isDisabled}
						type="text"
						displayOnly={isDisplay}
						condensed={isDisplay}
						noFeedBack={isDisplay}
						classNameField="c-pnp-flow-grid__ape2"
						name="apellido2"
						label="Segundo apellido"
						control={control}
						maxLength={150}
						error={errors.apellido2?.message}
						onBlur={(e) => {
							const limpio = Cleaners.texto(e.target.value);
							e.target.value = limpio;
							setValue('apellido2', limpio);
						}}
						onChange={(e) => {
							const limpio = Cleaners.texto(e.target.value);
							e.target.value = limpio;
							setValue('apellido2', limpio);
						}}
					/>
				</div>
				<div className="c-pnp-flow-grid">
					<CTNInputNest
						required={!isDisplay}
						displayOnly={isDisplay}
						condensed={isDisplay}
						noFeedBack={isDisplay}
						label="Teléfono"
						widthLeft="104"
						classNameField="c-pnp-flow-grid__tlf"
						error={errors.telefonoCodigoPais?.message ?? errors.telefonoNumero?.message}
					>
						<CTNDropdown
							filter
							disabled={isDisabled}
							displayOnly={isDisplay}
							options={prefijoPaises}
							name="telefonoCodigoPais"
							control={control}
							appendTo='body'
							error={errors.telefonoCodigoPais?.message}
						/>
						<CTNInput
							disabled={isDisabled}
							displayOnly={isDisplay}
							type="text"
							classNameField="c-pnp-input-numeric--left"
							name="telefonoNumero"
							control={control}
							maxLength={15}
							error={errors.telefonoNumero?.message}
							onBlur={(e) => {
								const limpio = Cleaners.numerico(e.target.value);
								e.target.value = limpio;
								setValue('telefonoNumero', limpio);
							}}
							onChange={(e) => {
								const limpio = Cleaners.numerico(e.target.value);
								e.target.value = limpio;
								setValue('telefonoNumero', limpio);
							}}
						/>
					</CTNInputNest>
					<CTNInput
						required={!isDisplay}
						disabled={isDisabled}
						type="text"
						displayOnly={isDisplay}
						condensed={isDisplay}
						noFeedBack={isDisplay}
						classNameField="c-pnp-flow-grid__mail"
						name="email"
						label="Correo electrónico"
						control={control}
						maxLength={254}
						error={errors.email?.message}
						onBlur={(e) => {
							const limpio = Cleaners.email(e.target.value);
							e.target.value = limpio;
							setValue('email', limpio);
						}}
						onChange={(e) => {
							const limpio = Cleaners.email(e.target.value);
							e.target.value = limpio;
							setValue('email', limpio);
						}}
					/>
				</div>
			</div>
		</fieldset>
	);
};

export default DatosPersona;
